//-- Copyright (c) 2021 TMT Insights Group, LLC
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { LoadingStatuses } from '@/constants/constants';
import { TitleEntity, VersionGroupedByParent } from '../slices/slice_types';
import { set } from 'lodash';

export const selectBulkSearch = (state: RootState) => state?.bulkSearch || {};
export const selectBulkAutoSubmit = createSelector(selectBulkSearch, (bulkSearch) => bulkSearch.autoSubmit);
export const selectBulkUploadedFile = createSelector(selectBulkSearch, (bulkSearch) => bulkSearch.file);
export const selectModalOrderId = createSelector(selectBulkSearch, (bulkSearch) => bulkSearch.modalOrderId);
export const selectBulkTitles = createSelector(selectBulkSearch, (bulkSearch) => bulkSearch.titles);
export const selectBulkTitleErrors = createSelector(selectBulkSearch, (bulkSearch) => bulkSearch.errors);
export const selectBulkTitleLoading = createSelector(selectBulkSearch, (bulkSearch) => bulkSearch.status === LoadingStatuses.pending);

export const selectGroupedBulkVersions = createSelector([selectBulkTitles], (selectedVersions): VersionGroupedByParent => {
  if (selectedVersions.length === 0) {
    return {};
  }

  const groupedVersions: VersionGroupedByParent = {};
  const data: TitleEntity[] = JSON.parse(JSON.stringify(selectedVersions));

  // for each version we have, created nested objects down to the version
  data.forEach((selectedVersion: TitleEntity) => {
    if (!selectedVersion.lineage || selectedVersion.lineage.length === 0) {
      // if there's no lineage, just add it to the root
      groupedVersions[`_${selectedVersion.id}`] = selectedVersion;
      return;
    }
    let lineage = [...selectedVersion.lineage].reverse();
    // add an underscore so lodash can recognize it as a key instead of an index
    lineage = lineage.map((id) => `_${id}`);
    // add the version id to the end of the lineage
    lineage.push(`_${selectedVersion.id}`);
    set(groupedVersions, lineage, selectedVersion);
  });

  return groupedVersions;
});